import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { Can, ReactTable } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { AddUpdateTyreBrandDiscountModal, DeleteTyreBrandDiscountModal } from "modules/TyreDiscountsDashboard/components";
import "modules/TyreDiscountsDashboard/components/TyreBrandDiscountsTable/TyreBrandDiscountsTable.scss";
import { PAGE_SIZE } from "utils/common";

import type { DropdownItemProps } from "components";
import type { TyreBrandDiscount, TyreDiscount } from "models";

type TyreBrandDiscountsTableProps = {
  brandOptions: DropdownItemProps[];
  categoriesOptions: DropdownItemProps[];
  discounts?: TyreDiscount[];
  brandDiscounts?: TyreBrandDiscount[];
  onCollapseRow: (rowId: string) => void;
};

const columnHelper = createColumnHelper<TyreBrandDiscount>();

export const TyreBrandDiscountsTable = ({ brandDiscounts = [], brandOptions, categoriesOptions, discounts, onCollapseRow }: TyreBrandDiscountsTableProps) => {
  const { t } = useTranslation();

  const [updateTyreBrandDiscountTyreId, setUpdateTyreBrandDiscountTyreId] = useState(0);
  const [updateTyreBrandDiscount, setUpdateTyreBrandDiscount] = useState<TyreBrandDiscount | null>(null);

  const [deleteTyreDiscountId, setDeleteTyreDiscountId] = useState(0);
  const [deleteTyreBrandDiscountId, setDeleteTyreBrandDiscountId] = useState(0);

  const handleSetIdsForDeleteTyreBrand = (tyreBrandDiscount: TyreBrandDiscount | null) => {
    setDeleteTyreDiscountId(tyreBrandDiscount ? tyreBrandDiscount.tyre_discount_id : 0);
    setDeleteTyreBrandDiscountId(tyreBrandDiscount ? tyreBrandDiscount.id : 0);
  };

  const handleSetUpdateTyreBrandDiscount = (tyreBrandDiscount: TyreBrandDiscount) => {
    setUpdateTyreBrandDiscount(tyreBrandDiscount);
    setUpdateTyreBrandDiscountTyreId(tyreBrandDiscount.tyre_discount_id);
  };

  const handleCloseUpdateTyreBrandDiscountModal = () => {
    setUpdateTyreBrandDiscountTyreId(0);
    setUpdateTyreBrandDiscount(null);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "brand",
        header: () => <span className="no-wrap-text">{t("v8_brand", "Brand")}</span>,
        size: 100,
        cell: ({
          row: {
            original: { active, brand }
          }
        }) => (
          <div className="table-name-container">
            <div className={`${active ? "active-discount" : "inactive-discount"}`} />
            <span className="no-wrap-text">{brand}</span>
          </div>
        )
      }),
      columnHelper.accessor(row => row, {
        id: "dummy_placeholder",
        header: () => null,
        size: 130,
        cell: () => null
      }),
      columnHelper.accessor(row => row, {
        id: "category",
        header: () => <span className="no-wrap-text">{t("v8_category", "Category")}</span>,
        size: 70,
        cell: ({
          row: {
            original: { category }
          }
        }) => {
          const categoryName = categoriesOptions.find(o => o.value === category)?.text;
          if (typeof categoryName !== "string") return <span className="no-wrap-text">{t("v8_unknown", "Unknown")}</span>;

          return <span className="no-wrap-text">{t(categoryName, categoryName)}</span>;
        }
      }),
      columnHelper.accessor(row => row, {
        id: "discount_on_price",
        header: () => <span className="no-wrap-text">{t("v8_discount_on_price", "Discount (On Price)")}</span>,
        size: 120,
        cell: ({
          row: {
            original: { discount }
          }
        }) => <span className="no-wrap-text"> {discount ? `${discount}%` : ""} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "net_margin",
        header: () => <span className="no-wrap-text">{`${t("v8_net", "Net")} + ${t("v8_margin", "Margin")}`}</span>,
        size: 100,
        cell: ({
          row: {
            original: { net_margin_euro }
          }
        }) => <span className="no-wrap-text"> {net_margin_euro ? net_margin_euro.toFixed(2) : ""} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "net_percent",
        header: () => <span className="no-wrap-text">{`${t("v8_net", "Net")} + ${t("v8_percent", "Percent")}`}</span>,
        size: 120,
        cell: ({
          row: {
            original: { net_margin_percent }
          }
        }) => <span className="no-wrap-text"> {net_margin_percent ? `${net_margin_percent}%` : ""} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "actions",
        size: 50,
        header: () => "",
        cell: ({ row: { original } }) => (
          <div className="actions-container">
            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.TyreDiscountBrands}>
              <div className="action-edit-button-container" onClick={() => handleSetUpdateTyreBrandDiscount(original)}>
                <Icon className="icon pen to square edit-icon action-button-icon-center" color="#32bf53" />
              </div>
            </Can>
            <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.TyreDiscountBrands}>
              <div className="action-button-container" onClick={() => handleSetIdsForDeleteTyreBrand(original)}>
                <Icon className="trash action-button-icon-center" color="red" />
              </div>
            </Can>
          </div>
        )
      })
    ],
    [t, categoriesOptions, handleSetUpdateTyreBrandDiscount, handleSetIdsForDeleteTyreBrand]
  );

  return (
    <>
      <div className="TyreBrandDiscountsTable">
        <ReactTable columns={columns} data={brandDiscounts} pageSize={PAGE_SIZE.PageSize25} />
      </div>
      <AddUpdateTyreBrandDiscountModal
        brandOptions={brandOptions}
        categoriesOptions={categoriesOptions}
        tyreBrandDiscount={updateTyreBrandDiscount}
        tyreBrandTyreDiscountId={updateTyreBrandDiscountTyreId}
        onClose={handleCloseUpdateTyreBrandDiscountModal}
      />
      <DeleteTyreBrandDiscountModal
        discounts={discounts}
        tyreDiscountId={deleteTyreDiscountId}
        tyreBrandDiscountId={deleteTyreBrandDiscountId}
        onCollapseRow={onCollapseRow}
        onResetDeleteTyreBrandDiscountId={() => handleSetIdsForDeleteTyreBrand(null)}
      />
    </>
  );
};
